import axios from "axios";
import { EVENTS_ENDPOINT_URL } from "../apiUrls";
import { resolveEventContext } from "./context";
import {
  clearPendingRedeemContext,
  consumeRecentInteractiveLogin,
  enqueueEventDraft,
  getBrowserSessionId,
  getEventQueue,
  getIdempotencyEntry,
  getPendingRedeemContext,
  hasSeenAccess,
  markAccessSeen,
  markRecentInteractiveLogin,
  normalizeStoredRedeemCode,
  removeQueuedEvent,
  removeStoredRedeemCode,
  setIdempotencyEntry,
  setPendingRedeemContext,
  updatePendingRedeemContext,
  upsertStoredRedeemCode,
} from "./storage";

export const EVENT_TYPES = {
  ACCESS: "access",
  CUSTOM_TERMS_CONFIRMED: "custom_terms_confirmed",
  LOGIN: "login",
  REGISTER: "register",
  REDEEM_MANUAL: "redeem_manual",
  REDEEM_QR: "redeem_qr",
  REQUEST: "request",
  TASK_RESULT: "task_result",
  TERMS_CONFIRMED: "terms_confirmed",
  VOTE: "vote",
};

const IDEMPOTENCY_TTL_BY_TYPE = {
  [EVENT_TYPES.ACCESS]: 12 * 60 * 60 * 1000,
  [EVENT_TYPES.CUSTOM_TERMS_CONFIRMED]: 180 * 24 * 60 * 60 * 1000,
  [EVENT_TYPES.LOGIN]: 15 * 60 * 1000,
  [EVENT_TYPES.REGISTER]: 10 * 365 * 24 * 60 * 60 * 1000,
  [EVENT_TYPES.REDEEM_MANUAL]: 365 * 24 * 60 * 60 * 1000,
  [EVENT_TYPES.REDEEM_QR]: 365 * 24 * 60 * 60 * 1000,
  [EVENT_TYPES.REQUEST]: 15 * 60 * 1000,
  [EVENT_TYPES.TASK_RESULT]: 24 * 60 * 60 * 1000,
  [EVENT_TYPES.TERMS_CONFIRMED]: 180 * 24 * 60 * 60 * 1000,
  [EVENT_TYPES.VOTE]: 15 * 60 * 1000,
  default: 24 * 60 * 60 * 1000,
};

let initialized = false;

const isBrowser = () => typeof window !== "undefined";

const buildBrowserEventMetadata = () => {
  if (!isBrowser()) return {};

  return {
    sessionId: getBrowserSessionId(),
    userAgent: window.navigator?.userAgent ?? null,
    landingUrl: window.location?.href ?? null,
  };
};

const createEventId = () => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (isBrowser() && window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const createEventAttemptId = () => {
  return createEventId().replace(/^evt_/, "attempt_");
};

const normalizeScalar = (value, fallback = "na") => {
  if (value === undefined || value === null) return fallback;

  const normalized = String(value).trim();
  return normalized === "" ? fallback : normalized;
};

const stableSerialize = (value) => {
  if (Array.isArray(value)) {
    return `[${value.map((entry) => stableSerialize(entry)).join(",")}]`;
  }

  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`)
      .join(",")}}`;
  }

  return JSON.stringify(value);
};

const hashValue = (value) => {
  const serialized = stableSerialize(value);
  let hash = 5381;

  for (let index = 0; index < serialized.length; index += 1) {
    hash = (hash * 33) ^ serialized.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
};

const normalizeSelectedOptions = (selectedOptions) => {
  if (!Array.isArray(selectedOptions)) return [];

  return selectedOptions
    .map((option) => normalizeScalar(option, ""))
    .filter(Boolean)
    .sort();
};

const resolveRedeemIdentifier = (payload) => {
  const candidates = [
    payload?.encodedCode,
    payload?.ccode,
    payload?.result?.ccode,
    payload?.result?.code,
    payload?.result?.coupon,
    payload?.result?.url,
  ];

  const matchedCandidate = candidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim() !== ""
  );

  if (matchedCandidate) {
    return matchedCandidate;
  }

  return `result_${hashValue(payload?.result ?? payload ?? {})}`;
};

const buildIdempotencyKey = ({
  eventId,
  type,
  payload,
  userSnapshot,
  radioSnapshot,
  attemptId,
  idempotencyScope,
  explicitIdempotencyKey,
}) => {
  if (explicitIdempotencyKey) {
    return explicitIdempotencyKey;
  }

  const userid = normalizeScalar(userSnapshot?.userid ?? userSnapshot?.id, "anonymous");
  const radioid = normalizeScalar(radioSnapshot?.radioid ?? radioSnapshot?.id, "unscoped");
  const scopeValue =
    typeof idempotencyScope === "string"
      ? idempotencyScope
      : idempotencyScope
        ? hashValue(idempotencyScope)
        : null;

  switch (type) {
    case EVENT_TYPES.ACCESS:
      return `access:${normalizeScalar(getBrowserSessionId(), "sessionless")}:${userid}:${radioid}`;
    case EVENT_TYPES.REGISTER:
      return `register:${userid}:${normalizeScalar(
        payload?.signup ?? payload?.signupAt ?? eventId,
        eventId
      )}`;
    case EVENT_TYPES.TERMS_CONFIRMED:
    case EVENT_TYPES.CUSTOM_TERMS_CONFIRMED:
      return `${type}:${userid}:${radioid}:${hashValue({
        selectedOptions: normalizeSelectedOptions(payload?.selectedOptions),
        form: payload?.form ?? [],
        values: payload?.values ?? {},
      })}`;
    case EVENT_TYPES.REDEEM_QR:
    case EVENT_TYPES.REDEEM_MANUAL:
      return `${type}:${userid}:${radioid}:${resolveRedeemIdentifier(payload)}`;
    default:
      return `${type}:${userid}:${radioid}:${normalizeScalar(
        attemptId ?? scopeValue ?? eventId,
        eventId
      )}`;
  }
};

const getIdempotencyTtlMs = (type) => {
  return IDEMPOTENCY_TTL_BY_TYPE[type] ?? IDEMPOTENCY_TTL_BY_TYPE.default;
};

const getDuplicateEntry = (draft) => {
  if (!draft?.idempotencyKey) return null;

  const entry = getIdempotencyEntry(draft.idempotencyKey);
  if (!entry) return null;

  if (entry.eventId === draft.eventId) {
    return entry.status === "sent" ? entry : null;
  }

  return ["pending", "queued", "sent"].includes(entry.status) ? entry : null;
};

const markDraftIdempotency = (draft, status) => {
  if (!draft?.idempotencyKey) return null;

  return setIdempotencyEntry(
    draft.idempotencyKey,
    {
      eventId: draft.eventId,
      type: draft.type,
      status,
    },
    getIdempotencyTtlMs(draft.type)
  );
};

const buildDraft = ({
  type,
  payload = {},
  user = null,
  radio = null,
  resolutionMode = "default",
  attemptId = null,
  idempotencyScope = null,
  idempotencyKey = null,
  occurredAt = null,
}) => {
  const eventId = createEventId();
  const enrichedPayload = {
    ...buildBrowserEventMetadata(),
    ...payload,
  };
  const userSnapshot = user
    ? {
        userid: user?.userid ?? user?.id ?? null,
        id: user?.id ?? null,
        email: user?.email ?? null,
      }
    : null;
  const radioSnapshot = radio ? { ...radio } : null;

  return {
    eventId,
    idempotencyKey: buildIdempotencyKey({
      eventId,
      type,
      payload: enrichedPayload,
      userSnapshot,
      radioSnapshot,
      attemptId,
      idempotencyScope,
      explicitIdempotencyKey: idempotencyKey,
    }),
    type,
    occurredAt: occurredAt ?? new Date().toISOString(),
    channel: "web",
    payload: enrichedPayload,
    userSnapshot,
    radioSnapshot,
    resolutionMode,
    attemptId,
  };
};

const buildEnvelope = (draft, resolvedContext) => ({
  eventId: draft.eventId,
  idempotencyKey: draft.idempotencyKey,
  type: draft.type,
  occurredAt: draft.occurredAt,
  channel: draft.channel,
  radioid: resolvedContext.radioContext.radioid,
  radioname: resolvedContext.radioContext.radioname,
  userid: resolvedContext.userContext.userid,
  email: resolvedContext.userContext.email,
  nameseo: resolvedContext.radioContext.nameseo,
  payload: draft.payload,
});

const dispatchEnvelope = async (envelope) => {
  await axios.post(EVENTS_ENDPOINT_URL, envelope);
};

const attemptDispatch = async (draft, providedContext = {}) => {
  const duplicateEntry = getDuplicateEntry(draft);

  if (duplicateEntry) {
    if (duplicateEntry.status === "sent") {
      removeQueuedEvent(draft.eventId, draft.idempotencyKey);
    }

    return {
      status: "skipped_duplicate_idempotency",
      draft,
      idempotencyKey: draft.idempotencyKey,
      duplicateStatus: duplicateEntry.status,
    };
  }

  markDraftIdempotency(draft, "pending");

  try {
    // Consent is bound to the account and radio that accepted it. A later
    // login or radio visit may flush this queue, but cannot reattribute it.
    const isConsent = [
      EVENT_TYPES.TERMS_CONFIRMED,
      EVENT_TYPES.CUSTOM_TERMS_CONFIRMED,
    ].includes(draft.type);
    const resolvedContext = await resolveEventContext({
      user: isConsent && draft.userSnapshot?.userid
        ? draft.userSnapshot
        : {
            ...(draft.userSnapshot ?? {}),
            ...(providedContext.user ?? {}),
          },
      radio: isConsent && draft.radioSnapshot?.radioid
        ? draft.radioSnapshot
        : {
            ...(draft.radioSnapshot ?? {}),
            ...(providedContext.radio ?? {}),
          },
      resolutionMode: draft.resolutionMode ?? "default",
    });

    if (!resolvedContext.complete) {
      enqueueEventDraft(draft);
      markDraftIdempotency(draft, "queued");
      return { status: "queued_missing_context", draft };
    }

    const envelope = buildEnvelope(draft, resolvedContext);

    try {
      await dispatchEnvelope(envelope);
      removeQueuedEvent(draft.eventId, draft.idempotencyKey);
      markDraftIdempotency(draft, "sent");
      return { status: "sent", envelope };
    } catch (error) {
      console.log("eventEmitter dispatch error", error);
      enqueueEventDraft(draft);
      markDraftIdempotency(draft, "queued");
      return { status: "queued_dispatch_error", error, envelope };
    }
  } catch (error) {
    console.log("eventEmitter context error", error);
    enqueueEventDraft(draft);
    markDraftIdempotency(draft, "queued");
    return { status: "queued_resolution_error", error, draft };
  }
};

export const flushQueuedEvents = async (providedContext = {}) => {
  const queue = getEventQueue();
  if (!queue.length) return [];

  const results = [];
  for (const draft of queue) {
    // Process sequentially to preserve emission order and avoid duplicate retries.
    // This queue is expected to stay small in the browser.
    // eslint-disable-next-line no-await-in-loop
    const result = await attemptDispatch(draft, providedContext);
    results.push(result);
  }

  return results;
};

export const initializeEventEmitter = () => {
  if (!isBrowser() || initialized) return;

  initialized = true;
  window.addEventListener("online", () => {
    void flushQueuedEvents();
  });

  void flushQueuedEvents();
};

export const reportEvent = async ({
  type,
  payload = {},
  user = null,
  radio = null,
  resolutionMode = "default",
  attemptId = null,
  idempotencyScope = null,
  idempotencyKey = null,
  occurredAt = null,
}) => {
  const draft = buildDraft({
    type,
    payload,
    user,
    radio,
    resolutionMode,
    attemptId,
    idempotencyScope,
    idempotencyKey,
    occurredAt,
  });

  const result = await attemptDispatch(draft, { user, radio });

  if (result.status === "sent") {
    void flushQueuedEvents({ user, radio });
  }

  return result;
};

export const rememberInteractiveLogin = (user) => {
  markRecentInteractiveLogin(user);
};

export const reportAccessEvent = async ({ user, radio, payload = {} }) => {
  const userid = user?.userid ?? user?.id ?? null;
  const radioid = radio?.radioid ?? radio?.id ?? null;

  if (consumeRecentInteractiveLogin()) {
    await flushQueuedEvents({ user, radio });
    return { status: "skipped_recent_login" };
  }

  if (hasSeenAccess(userid, radioid)) {
    await flushQueuedEvents({ user, radio });
    return { status: "skipped_duplicate_session" };
  }

  markAccessSeen(userid, radioid);

  return reportEvent({
    type: EVENT_TYPES.ACCESS,
    payload,
    user,
    radio,
  });
};

export const rememberRedeemCapture = ({
  encodedCode,
  source,
  sourceDetail = null,
  capturedAt = Date.now(),
  radioHint = null,
}) => {
  const normalizedEntry = normalizeStoredRedeemCode({
    encodedCode,
    source,
    sourceDetail,
    capturedAt,
    radioHint,
  });

  if (!normalizedEntry) return null;

  upsertStoredRedeemCode(normalizedEntry);
  setPendingRedeemContext({
    encodedCode: normalizedEntry.encodedCode,
    source: normalizedEntry.source,
    sourceDetail: normalizedEntry.sourceDetail,
    capturedAt: normalizedEntry.capturedAt,
    radioHint: normalizedEntry.radioHint,
  });

  return normalizedEntry;
};

export const updateRedeemCaptureContext = ({
  encodedCode,
  radioHint,
  source,
  sourceDetail,
}) => {
  const updatedEntry = upsertStoredRedeemCode({
    encodedCode,
    source,
    sourceDetail,
    radioHint,
  });

  updatePendingRedeemContext({
    encodedCode,
    source: source ?? updatedEntry?.source ?? getPendingRedeemContext()?.source,
    sourceDetail:
      sourceDetail ??
      updatedEntry?.sourceDetail ??
      getPendingRedeemContext()?.sourceDetail,
    radioHint: radioHint ?? updatedEntry?.radioHint,
  });
};

const isSuccessfulResult = (result) => {
  if (!result) return false;
  if (typeof result?.status === "string") {
    return result.status === "success";
  }
  return true;
};

export const reportRedeemSuccess = async ({
  result,
  user,
  radio = null,
  source,
  code = null,
  encodedCode = null,
  idempotencyKey = null,
}) => {
  if (!isSuccessfulResult(result)) {
    return { status: "skipped_unsuccessful_redeem" };
  }

  const pendingContext = getPendingRedeemContext();
  const resolvedSource = source ?? pendingContext?.source ?? "manual";
  const eventType =
    resolvedSource === "qr"
      ? EVENT_TYPES.REDEEM_QR
      : EVENT_TYPES.REDEEM_MANUAL;

  const responsePayload = result?.data ? result.data : result;
  const storedCodesToRemove = [
    pendingContext?.encodedCode,
    encodedCode,
  ].filter(Boolean);

  storedCodesToRemove.forEach((storedCode) => {
    removeStoredRedeemCode(storedCode);
  });

  if (pendingContext?.encodedCode) {
    clearPendingRedeemContext();
  }

  const reportResult = await reportEvent({
    type: eventType,
    user,
    radio: radio ?? pendingContext?.radioHint ?? null,
    payload: {
      source: resolvedSource,
      sourceDetail: pendingContext?.sourceDetail ?? null,
      encodedCode: encodedCode ?? pendingContext?.encodedCode ?? null,
      ccode: code,
      result: responsePayload,
    },
    idempotencyKey,
  });

  return reportResult;
};
